import { PROPERTY_ID, trackJourneyEvent } from '../apiCalls';

const ONCE_PER_JOURNEY = new Set([
    'calculator_viewed',
    'form_started',
    'calculation_completed',
    'results_viewed',
    'lead_form_opened',
    'lead_submitted',
    'quote_delivered',
]);
const JOURNEY_EVENTS = new Set([...ONCE_PER_JOURNEY, 'step_viewed', 'journey_exited']);
const FIELD_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,59}$/;
const USER_INTERACTION_WINDOW_MS = 1500;
const state = { journeyId: null, sent: null, step: null, lastField: null, lastInteractionAt: 0, layout: 'single' };

const readSession = (key) => {
    try { return window.sessionStorage.getItem(key); } catch (_error) { return null; }
};

const writeSession = (key, value) => {
    try { window.sessionStorage.setItem(key, value); } catch (_error) { /* Keep state in memory when storage is blocked. */ }
};

const createUuid = () => {
    const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;
    if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
    const bytes = new Uint8Array(16);
    if (cryptoApi?.getRandomValues) cryptoApi.getRandomValues(bytes);
    else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const getJourneyId = () => {
    if (!state.journeyId) {
        state.journeyId = readSession('solarCalcJourneyId') || createUuid();
        writeSession('solarCalcJourneyId', state.journeyId);
    }
    return state.journeyId;
};

const getSentKeys = () => {
    if (!state.sent) {
        try { state.sent = new Set(JSON.parse(readSession('solarCalcJourneySent') || '[]')); } catch (_error) { state.sent = new Set(); }
    }
    return state.sent;
};

const markSent = (key) => {
    const sent = getSentKeys();
    sent.add(key);
    writeSession('solarCalcJourneySent', JSON.stringify([...sent]));
};

const isTrackingDisabled = () => typeof window === 'undefined'
    || !PROPERTY_ID
    || Boolean(window.Cypress)
    || Boolean(window.navigator?.webdriver);

const toFieldKey = (field) => {
    if (typeof field !== 'string' || !field) return undefined;
    return FIELD_KEY_PATTERN.test(field) ? field : 'other';
};

const send = (payload, options) => {
    try {
        trackJourneyEvent(payload, options);
    } catch (_error) {
        // Journey tracking must not interrupt calculator use.
    }
};

export const trackJourney = (eventType, { step, field } = {}) => {
    if (isTrackingDisabled() || !JOURNEY_EVENTS.has(eventType)) return;
    if (eventType === 'step_viewed' && (!Number.isInteger(step) || step < 1 || step > 20)) return;

    if (eventType === 'step_viewed') {
        state.step = step;
        const key = `step_viewed:${step}`;
        if (getSentKeys().has(key)) return;
        markSent(key);
    } else if (eventType !== 'journey_exited' && ONCE_PER_JOURNEY.has(eventType)) {
        if (getSentKeys().has(eventType)) return;
        markSent(eventType);
    }

    const payload = {
        propertyID: PROPERTY_ID,
        journeyId: getJourneyId(),
        eventId: createUuid(),
        eventType,
        layout: state.layout,
        ...(Number.isInteger(step) && step > 0 ? { step } : {}),
        ...(toFieldKey(field) ? { field: toFieldKey(field) } : {}),
        ...(process.env.REACT_APP_RELEASE || process.env.REACT_APP_GIT_SHA
            ? { calculatorVersion: process.env.REACT_APP_RELEASE || process.env.REACT_APP_GIT_SHA }
            : {}),
    };
    send(payload, { beacon: eventType === 'journey_exited' });
};

export const markJourneyInteraction = () => {
    state.lastInteractionAt = Date.now();
};

export const startJourneyForm = () => {
    markJourneyInteraction();
    trackJourney('form_started');
};

export const noteJourneyField = (field) => {
    if (!field || Date.now() - state.lastInteractionAt > USER_INTERACTION_WINDOW_MS) return;
    state.lastField = toFieldKey(field) || null;
};

export const initJourneyExitTracking = () => {
    if (typeof document === 'undefined') return () => { };
    let exitSent = false;
    const sendExit = () => {
        if (exitSent || isTrackingDisabled()) return;
        exitSent = true;
        trackJourney('journey_exited', { step: state.step, field: state.lastField });
    };
    const onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') sendExit();
        else exitSent = false;
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', sendExit);
    return () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pagehide', sendExit);
    };
};

export const resetJourneyStateForTests = () => {
    state.journeyId = null;
    state.sent = null;
    state.step = null;
    state.lastField = null;
    state.lastInteractionAt = 0;
};